/* ============================================================
   useProfileGuard —— "开始测评" 前的画像 gate hook
   ------------------------------------------------------------
   返回 status 字符串四态：
     'pass'                — 画像完整（industry / position / ai_usage_purpose 都非空）
     'blocked_incomplete'  — 三字段任一空，已 setGuardOpen(true)，由 modal 提示
     'blocked_auth'        — 401 落到 hook（axios 拦截器 refresh 已失败或无 refresh token），
                             调用方直接 return 让拦截器接管跳 /login，不显示任何 modal/error
     'blocked_error'       — 5xx / 网络断 / 解析异常，调用方负责显示重试提示

   为什么 401 不当 pass：axios 拦截器（client.js:48-65）已经在做 refresh + retry。
     refresh 成功 → 调用方拿 200，hook 永远不会 catch 到 401。
     hook 真 catch 到 401 = refresh 失败或无 token，拦截器自己跳 login；
     此时放行会让后续 createAssessment 再挂一次，产生更晚更模糊的失败。

   为什么 5xx / 网络断不放行：后端 views.py:58 创建测评时把空字段当默认快照，
     guard 放行就等于让后端继续生成 "未填写" 题目，gate 形同虚设。

   alive ref：跟 ProfileNudgeModal 一致的卸载保护，避免 fetch 在组件卸载后
     落回时 setGuardOpen 触发 React 警告。
   ============================================================ */
import { useCallback, useEffect, useRef, useState } from 'react';
import client from '../api/client';

export default function useProfileGuard() {
  const [guardOpen, setGuardOpen] = useState(false);
  const alive = useRef(true);

  useEffect(() => {
    alive.current = true;
    return () => { alive.current = false; };
  }, []);

  const checkAndGuard = useCallback(async () => {
    try {
      const { data } = await client.get('/profile/');
      const incomplete =
        !(data.industry || '').trim() ||
        !(data.position || '').trim() ||
        !(data.ai_usage_purpose || '').trim();
      if (incomplete) {
        if (alive.current) setGuardOpen(true);
        return 'blocked_incomplete';
      }
      return 'pass';
    } catch (err) {
      if (err?.response?.status === 401) return 'blocked_auth';
      return 'blocked_error';
    }
  }, []);

  const closeGuard = useCallback(() => setGuardOpen(false), []);

  return { guardOpen, checkAndGuard, closeGuard };
}
